import clsx from "clsx";
import {
  Archive,
  Download,
  EllipsisVertical,
  LucideIcon,
  RefreshCcw,
  Trash2,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { Document, DocumentStatus } from "~/api/model";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useProjectParams } from "~/helpers";

import { useQueryClient } from "@tanstack/react-query";
import {
  getListDocumentsQueryKey,
  useGetDocumentDownloadUrl,
} from "~/api/default/default";
import { useRestartDocumentPipeline } from "~/api/documents/documents";
import { trackEvent } from "~/utils/analytics";
import { useDocumentSelectionStore } from "../../stores/documentSelectionStore";
import { ArchiveDocumentAlertDialog } from "../ArchiveDocumentAlertDialog";
import { DeleteDocumentAlertDialog } from "../DeleteDocumentAlertDialog";

interface CaseDocumentActionsProps {
  document: Document;
  onCancelDraftUpload?: (filename: string) => void;
}

interface ActionItem {
  id: string;
  icon: LucideIcon;
  messageId: string;
  defaultMessage: string;
  className?: string;
  action: () => void;
}

export const CaseDocumentActions: FC<CaseDocumentActionsProps> = ({
  document,
  onCancelDraftUpload,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const { mutateAsync: getDocumentDownloadUrl } = useGetDocumentDownloadUrl();
  const { mutateAsync: restartDocumentPipeline } = useRestartDocumentPipeline();
  const queryClient = useQueryClient();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showArchiveAlertDialog, setShowArchiveAlertDialog] = useState(false);
  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);

  useEffect(() => {
    if (dropdownOpen) {
      trackEvent({
        eventName: "app/case/actions.button/click",
        properties: { caseId, documentId: document.id },
      });
    }
  }, [dropdownOpen, document.id, caseId]);

  const handleDownloadDocument = async () => {
    trackEvent({
      eventName: "app/case/actions_menu/download.link/click",
      properties: { caseId, documentId: document.id },
    });

    const { url } = await getDocumentDownloadUrl({
      organizationId,
      projectId: caseId,
      documentId: document.id,
      params: {
        update_counter: true,
      },
    });

    window.open(url, "_blank");
  };

  const handleRetryDocument = async () => {
    trackEvent({
      eventName: "app/case/actions_menu/reprocess.link/click",
      properties: { caseId, documentId: document.id },
    });

    await restartDocumentPipeline({
      organizationId,
      projectId: caseId,
      documentId: document.id,
    });

    await queryClient.invalidateQueries({
      queryKey: getListDocumentsQueryKey(organizationId, caseId),
    });

    useDocumentSelectionStore.getState().clearSelection();
  };

  const documentProcessingStarted =
    document.status === DocumentStatus.PROCESSING ||
    document.status === DocumentStatus.COMPLETE ||
    document.status === DocumentStatus.ERROR;

  const actionItems = [
    document.status === DocumentStatus.DRAFT && {
      id: "delete",
      icon: Trash2,
      messageId: "caseDocument.actionsDropdown.delete",
      action: () => {
        if (document.id.startsWith("queued-")) {
          onCancelDraftUpload?.(document.filename);
        } else {
          setShowDeleteAlertDialog(true);
        }
      },
      defaultMessage: "Delete",
    },
    documentProcessingStarted && {
      id: "retry",
      icon: RefreshCcw,
      messageId: "caseDocument.actionsDropdown.retry",
      action: handleRetryDocument,
      defaultMessage: "Re-process",
    },
    documentProcessingStarted && {
      id: "download",
      icon: Download,
      messageId: "caseDocument.actionsDropdown.download",
      action: handleDownloadDocument,
      defaultMessage: "Download",
    },
    document.status !== DocumentStatus.DRAFT && {
      id: "archive",
      icon: Archive,
      messageId: "archive",
      action: () => {
        trackEvent({
          eventName: "app/case/actions_menu/archive.link/click",
          properties: { caseId, documentId: document.id },
        });
        setShowArchiveAlertDialog(true);
      },
      defaultMessage: "Archive",
    },
  ].filter(Boolean) as ActionItem[];

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={"hover:opacity-80 rounded-full"}
          >
            <EllipsisVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-32" align="end">
          {actionItems.map((actionItem) => {
            return (
              <DropdownMenuItem
                key={actionItem.id}
                className={clsx("cursor-pointer", actionItem.className)}
                onClick={actionItem.action}
                destructive={actionItem.id === "delete"}
              >
                <actionItem.icon className="mr-2 h-4 w-4" />
                <FormattedMessage
                  id={actionItem.messageId}
                  defaultMessage={actionItem.defaultMessage}
                />
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {showArchiveAlertDialog && (
        <ArchiveDocumentAlertDialog
          open={showArchiveAlertDialog}
          setOpen={setShowArchiveAlertDialog}
          documentId={document.id}
          documentName={document.filename}
          caseId={caseId}
        />
      )}
      {showDeleteAlertDialog && (
        <DeleteDocumentAlertDialog
          open={showDeleteAlertDialog}
          setOpen={setShowDeleteAlertDialog}
          documentId={document.id}
          documentName={document.filename}
          caseId={caseId}
        />
      )}
    </>
  );
};
